import { Descriptions, Input, Select, Switch, Tag, Tooltip } from 'antd'
import { toTitleCase } from 'features/dynamicOntology'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { getEnumColorMap } from 'features/libra/ontology/enumColors'
import {
  getFieldSource,
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldCategory,
  FieldValues,
  FieldValueSource,
  InputField,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { FormSection } from 'features/libra/sections/formSection/formSectionTypes'
import { ReadOnlySourceDisplay } from 'features/libra/stepComponents/conditionalForm/conditionalFormSection/SourceInput'
import { isAiUnextracted } from 'features/libra/stepComponents/GongAgentAutofill'
import { StyledCard } from 'features/libra/stepComponents/niiReport/cards/cardsStyles'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react'
import styled from 'styled-components'

import { DynamicFieldTooltip } from './tooltips/DynamicFieldTooltip'

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const LabelWithTooltipContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

interface NiiScreeningFormSectionProps {
  config: FormSection
  ontologyFields: Record<string, OntologyField>
  fieldValues: FieldValues
  setFieldValues: Dispatch<SetStateAction<FieldValues>>
  disabled?: boolean
  stepId: string
}

/**
 * Get the input field from ontology, filtering out non-input fields
 */
function getInputField(
  ontologyFields: Record<string, OntologyField>,
  fieldId: string
): InputField | undefined {
  const field = ontologyFields[fieldId]
  if (field && field.category === FieldCategory.INPUT) {
    return field as InputField
  }
  return undefined
}

/**
 * Renders a form section as a card with field inputs in Descriptions format.
 * This preserves the NII Screening card-based UI while being config-driven.
 */
export const NiiScreeningFormSection: FC<NiiScreeningFormSectionProps> = ({
  config,
  ontologyFields,
  fieldValues,
  setFieldValues,
  disabled: disabledProp,
  stepId,
}) => {
  const isDisabled = disabledProp ?? false
  const title = config.title ?? config.id

  // Handler for updating a field value - wraps primitives back into { value, source }
  const handleFieldChange = useCallback(
    (fieldId: string) => (value: unknown) => {
      setFieldValues((prev) => {
        // A clear is an explicit null write: a removed key would be restored
        // from the persisted values on save and the clear would never stick.
        const isCleared = value === undefined || value === null || value === ''
        // Preserve existing source when updating the value
        const existingSource = getFieldSource(prev[fieldId])
        return {
          ...prev,
          [fieldId]: wrapFieldValue(isCleared ? null : value, existingSource),
        }
      })
    },
    [setFieldValues]
  )

  // Get filtered fields with their ontology definitions
  const fields = useMemo(() => {
    return config.fields
      .map((fieldId) => {
        const ontologyField = getInputField(ontologyFields, fieldId)
        return { fieldId, ontologyField }
      })
      .filter(
        (f): f is { fieldId: string; ontologyField: InputField } =>
          f.ontologyField !== undefined
      )
  }, [config.fields, ontologyFields])

  // Render a field label with optional tooltip
  const renderFieldLabel = (field: InputField) => {
    // Use tooltip object if available
    if (field.tooltip) {
      return (
        <LabelWithTooltipContainer>
          {field.name}
          <DynamicFieldTooltip tooltip={field.tooltip} />
        </LabelWithTooltipContainer>
      )
    }

    return field.name
  }

  return (
    <StyledCard title={title}>
      <Descriptions column={1} bordered>
        {fields.map(({ fieldId, ontologyField }) => {
          const unextracted = isAiUnextracted(fieldValues[fieldId])
          return (
            <Descriptions.Item
              key={fieldId}
              label={renderFieldLabel(ontologyField)}>
              <CommentIndicator anchor={{ type: 'field', stepId, fieldId }}>
                <Tooltip
                  title={
                    unextracted
                      ? 'AI could not find data for this field in the available documents'
                      : undefined
                  }>
                  <NiiFieldWrapper $unextracted={unextracted}>
                    <FieldRenderer
                      field={ontologyField}
                      value={unwrapFieldValue(fieldValues[fieldId])}
                      source={getFieldSource(fieldValues[fieldId])}
                      onChange={handleFieldChange(fieldId)}
                      disabled={isDisabled}
                    />
                  </NiiFieldWrapper>
                </Tooltip>
              </CommentIndicator>
            </Descriptions.Item>
          )
        })}
      </Descriptions>
    </StyledCard>
  )
}

interface FieldRendererProps {
  field: InputField
  value: unknown
  source: FieldValueSource
  onChange: (value: unknown) => void
  disabled: boolean
}

const FIELD_RENDERERS: Record<
  string,
  (props: {
    field: InputField
    value: unknown
    onChange: (value: unknown) => void
  }) => React.ReactNode
> = {
  boolean: ({ value, onChange }) => (
    <SwitchRow>
      <Switch checked={!!value} onChange={(checked) => onChange(checked)} />
      <span>{value ? 'Yes' : 'No'}</span>
    </SwitchRow>
  ),
  enum: ({ field, value, onChange }) => (
    <EnumSelect
      value={value as string | undefined}
      onChange={onChange}
      allowedValues={field.allowedValues ?? []}
      inverted={field.invertedColors}
    />
  ),
  text: ({ field, value, onChange }) => (
    <Input.TextArea
      value={value as string | undefined}
      onChange={(e) => onChange(e.target.value)}
      rows={2}
      placeholder={`Enter ${field.name.toLowerCase()}...`}
    />
  ),
  number: ({ field, value, onChange }) => (
    <Input
      type='number'
      value={value as number | undefined}
      onChange={(e) => onChange(Number(e.target.value))}
      placeholder={`Enter ${field.name.toLowerCase()}...`}
    />
  ),
  date: ({ value, onChange }) => (
    <Input
      type='date'
      value={value as string | undefined}
      onChange={(e) => onChange(e.target.value)}
    />
  ),
}

/**
 * Renders an individual field based on its type from the ontology definition.
 */
const FieldRenderer: FC<FieldRendererProps> = ({
  field,
  value,
  source,
  onChange,
  disabled,
}) => {
  if (disabled) {
    return <ReadOnlyValue value={value} field={field} source={source} />
  }

  const renderer = FIELD_RENDERERS[field.type]
  if (renderer) {
    return <>{renderer({ field, value, onChange })}</>
  }

  // Default fallback
  return (
    <Input
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

interface EnumSelectProps {
  value: string | undefined
  onChange: (value: unknown) => void
  allowedValues: ReadonlyArray<string>
  inverted?: boolean
}

const EnumSelect: FC<EnumSelectProps> = ({
  value,
  onChange,
  allowedValues,
  inverted = false,
}) => {
  const colorMap = getEnumColorMap(inverted)

  const options = useMemo(
    () =>
      allowedValues.map((v) => ({
        value: v,
        label: toTitleCase(v),
      })),
    [allowedValues]
  )

  return (
    <Select
      value={value}
      onChange={onChange}
      style={{ minWidth: 120 }}
      allowClear
      placeholder='Select...'>
      {options.map((opt) => (
        <Select.Option key={opt.value} value={opt.value}>
          {colorMap[opt.value] ? (
            <Tag color={colorMap[opt.value]}>{opt.label}</Tag>
          ) : (
            opt.label
          )}
        </Select.Option>
      ))}
    </Select>
  )
}

interface ReadOnlyValueProps {
  value: unknown
  field: InputField
  source: FieldValueSource
}

const NiiFieldWrapper = styled.div<{ $unextracted: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${({ $unextracted }) =>
    $unextracted &&
    `
    .ant-input,
    .ant-input-affix-wrapper,
    .ant-select-selector,
    .ant-picker,
    input,
    textarea {
      border-color: #faad14 !important;
      box-shadow: 0 0 0 2px rgba(250, 173, 20, 0.2) !important;
    }
  `}
`

const ReadOnlyFieldValue = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const ReadOnlyValue: FC<ReadOnlyValueProps> = ({ value, field, source }) => {
  const showSource = field.includeSource && source

  if (value === undefined || value === null) {
    return (
      <ReadOnlyFieldValue>
        <span style={{ color: '#999' }}>—</span>
        {showSource && <ReadOnlySourceDisplay source={source} />}
      </ReadOnlyFieldValue>
    )
  }

  let displayValue: React.ReactNode
  if (typeof value === 'boolean') {
    displayValue = <span>{value ? 'Yes' : 'No'}</span>
  } else if (field.type === 'enum') {
    const strValue = String(value)
    const colorMap = getEnumColorMap(field.invertedColors)
    const color = colorMap[strValue]
    const label = toTitleCase(strValue)
    displayValue = color ? (
      <Tag color={color}>{label}</Tag>
    ) : (
      <span>{label}</span>
    )
  } else {
    displayValue = <span>{String(value)}</span>
  }

  return (
    <ReadOnlyFieldValue>
      {displayValue}
      {showSource && <ReadOnlySourceDisplay source={source} />}
    </ReadOnlyFieldValue>
  )
}

export default React.memo(NiiScreeningFormSection)
